import { useToast } from "@expert/common-ui";
import { useFeatures, type WorkspaceFeatures } from "@expert/features";
import { getLogger } from "@expert/logging";
import { Flex, Group, Modal, Stack, Text } from "@mantine/core";
import { useClipboard, useDisclosure } from "@mantine/hooks";
import { type JSX, useCallback, useEffect, useState } from "react";
import { useTimer } from "react-timer-hook";
import { useShallow } from "zustand/react/shallow";
import { ScheduleCallbackForm } from "@/call-controls";
import { usePartnerConfig, usePartnerFeatureConfigured } from "@/partner/usePartnerConfig";
import { isVoiceTask, useAgentSdk, useAgentStore, useCallbacksRemaining, usePartner, useSession } from "@/sdk";
import { useCallbackStore } from "@/sdk/sessions/callback.store";
import { useSessionSummaryStore } from "../store/useSessionSummaryStore";
import { getPartnerTool } from "../utils";
import { CustomerDroppedNotification } from "./CustomerDroppedNotification";
import styles from "./EditSessionSummaryModal.module.css";
import { SessionSummaryActions } from "./SessionSummaryActions";
import { SessionSummaryForm } from "./SessionSummaryForm";
import { useSummary } from "./useSummary";

const logger = getLogger({ module: "NewSessionSummary", supportTeam: "solve" });

export function SessionSummary(): JSX.Element | null {
    const clipboard = useClipboard({ timeout: 500 });
    const agentSdk = useAgentSdk();
    const partner = usePartner();
    const toast = useToast();
    const { wrappingAgentActivity } = usePartnerConfig();

    const [setCanAppend] = useSessionSummaryStore((state) => [state.setCanAppend]);

    const [view, setView] = useState<"edit-summary" | "schedule-callback">("edit-summary");
    const [summaryLoaded, setSummaryLoaded] = useState(false);
    const [summaryGenerationTimedOut, setSummaryGenerationTimedOut] = useState(false);
    const [isCopied, setIsCopied] = useState<boolean>(false);
    const [finalTimestamp, setFinalTimestamp] = useState<number>(Date.now());
    const {
        features: { isAutoSummaryEnabled },
    } = useFeatures<WorkspaceFeatures>();
    const { isCustomerDroppedNotificationEnabled } = usePartnerFeatureConfigured();
    const { gaiaSummaryModel } = usePartnerConfig();
    const [opened, { open: openModal, close: closeModal }] = useDisclosure(false);

    const customerEndedCall = useSessionSummaryStore((state) => state.customerEndedCall);
    const showCallEndedBanner = customerEndedCall && isCustomerDroppedNotificationEnabled;

    const { activity, pendingActivity } = useAgentStore(useShallow((state) => state));
    const activeSession = useSession();
    const activeTask = isVoiceTask(activeSession.currentTask) ? activeSession.currentTask : null;

    const agentCallSid =
        activeTask?.agentCallId ??
        Array.from(activeSession.tasks.values())
            .filter(isVoiceTask)
            .findLast((task) => task.agentCallId !== undefined)?.agentCallId;

    const {
        summaryGenerated,
        validAccountNumber,
        requiresManualCopy,
        sessionSummaryTimes,
        summary,
        setSessionSummaryFromGaia,
        onDismiss,
        getExtraSummaryTime,
    } = useSummary();

    const shouldAutoGenerateSummary = activeTask?.locale !== "es-mx";
    const shouldSendSummaryToPartnerTool = validAccountNumber && (!requiresManualCopy || isAutoSummaryEnabled);
    const needsCopy = !shouldSendSummaryToPartnerTool && !isCopied;
    const sessionSummaryLoading =
        (!summaryLoaded || !summaryGenerationTimedOut) && shouldAutoGenerateSummary && !!gaiaSummaryModel;
    const canSendSuccessfulSummary = summaryGenerated && shouldSendSummaryToPartnerTool;
    const shouldShowTextArea = !sessionSummaryLoading && !isCopied && !canSendSuccessfulSummary;
    const shouldDisableTextArea = summaryGenerated && needsCopy;

    // A wrapping task or a newly wrapping agent means we are entering session summary
    const inWrappingState = activeTask?.status === "wrapping" || activity === wrappingAgentActivity;
    const taskIsActive = activeTask?.status === "assigned";
    const { callbackState } = useCallbackStore();
    const { canCallback, callbacksRemaining } = useCallbacksRemaining();
    const hasImmediateCallBack = callbackState?.callbackType === "CallbackNow";

    const baseInitialTimer = activeSession.wrappingState?.expirationTimestamp ?? Date.now();
    const initialTimer = shouldAutoGenerateSummary ? baseInitialTimer : baseInitialTimer + sessionSummaryTimes.maximum;

    const endSession = useCallback(
        async (reason: "AgentTerminatedWrapping" | "WrappingTimeExpired") => {
            logger.trace({ reason, partner }, "SessionSummary | Ending session wrapup");
            await agentSdk.endWrapup(reason, partner, activeTask!);
        },
        [activeTask, agentSdk, partner],
    );

    async function handleWrappingExpired() {
        if (needsCopy) copySummary();
        await endSession("WrappingTimeExpired");
    }

    const copySummary = () => {
        try {
            clipboard.copy(summary);
            setIsCopied(true);
            toast.success("Session summary copied to clipboard");
        } catch (err) {
            logger.error({ err }, "SessionSummary | Error copying session summary");
        }
    };

    async function handleCopyOrEndSessionClick() {
        if (needsCopy) copySummary();
        else await endSession("AgentTerminatedWrapping");
    }

    /** Prevent mantine's attempt to close the modal when the outside overlay is clicked */
    const interceptClose = () => null;
    const callbackScheduleHandler = async () => await onDismiss(closeModal, agentCallSid, { checkSummary: false });

    function getSummaryHeader() {
        if (summaryGenerationTimedOut) {
            if (requiresManualCopy) {
                if (!summaryGenerated) {
                    return "Oh no—we couldn’t generate a summary";
                }
                return `Add notes to ${getPartnerTool(partner)}`;
            }
            if (!validAccountNumber) {
                return `Autosend failed—add notes to ${getPartnerTool(partner)}`;
            } else if (!summaryGenerated) {
                return "Oh no—we couldn’t generate a summary";
            }
        }
        return "Wrap up";
    }

    function getSummaryButtonText() {
        if (summaryGenerationTimedOut) {
            if (needsCopy) {
                return "Copy notes";
            } else if (pendingActivity === agentSdk.offlineAgentActivity) {
                return "End session and log off";
            } else if (!summaryGenerated && shouldSendSummaryToPartnerTool) {
                return "Save and end session";
            }
            return "End session";
        }
        return "Generating";
    }

    const { totalSeconds, restart } = useTimer({
        expiryTimestamp: new Date(initialTimer),
        autoStart: true,
        onExpire: () => {
            if (summaryGenerationTimedOut || !shouldAutoGenerateSummary) void handleWrappingExpired();
            else restartTimer();
        },
    });

    function restartTimer() {
        const timestamp = activeSession.wrappingState?.expirationTimestamp
            ? activeSession.wrappingState.expirationTimestamp +
              getExtraSummaryTime(canSendSuccessfulSummary, shouldDisableTextArea)
            : Date.now();
        setFinalTimestamp(timestamp);
        setSummaryGenerationTimedOut(true);
        setTimeout(() => {
            restart(new Date(timestamp), true);
        }, 50);
    }

    useEffect(() => {
        if (summaryLoaded && shouldAutoGenerateSummary) {
            restartTimer();
        }
        // oxlint-disable-next-line exhaustive-deps
    }, [summaryLoaded, shouldAutoGenerateSummary]);

    useEffect(() => setView("edit-summary"), [opened]);

    useEffect(() => {
        if (!shouldAutoGenerateSummary) {
            setSummaryLoaded(true);
            setSummaryGenerationTimedOut(true);
            return;
        }

        if (opened && inWrappingState) {
            void setSessionSummaryFromGaia(setSummaryLoaded, agentCallSid);
        }
    }, [setSessionSummaryFromGaia, inWrappingState, shouldAutoGenerateSummary, opened, agentCallSid]);

    useEffect(() => {
        if (hasImmediateCallBack) return;

        if (inWrappingState) {
            openModal();
        } else if (taskIsActive) {
            setCanAppend(true);
        }
    }, [hasImmediateCallBack, inWrappingState, taskIsActive, setCanAppend, openModal]);

    return (
        <Group className={styles.modalContainer}>
            <Modal
                centered
                transitionProps={{
                    transition: "pop",
                }}
                classNames={{
                    inner: styles.modalInner,
                    overlay: styles.modalOverlay,
                    content: styles.modalContent,
                }}
                onClose={interceptClose}
                opened={opened}
                overlayProps={{
                    backgroundOpacity: 0.1,
                }}
                size="32rem"
                withCloseButton={false}
                title={showCallEndedBanner ? <CustomerDroppedNotification /> : null}
                styles={{
                    title: {
                        padding: 0,
                        margin: 0,
                        display: "flex",
                        flex: 1,
                        width: "100%",
                        minHeight: "none",
                    },
                    header: {
                        padding: 0,
                        margin: 0,
                        display: "flex",
                        flex: 1,
                        width: "100%",
                        minHeight: "none",
                        marginBottom: "var(--mantine-spacing-xs)",
                    },
                    body: {
                        padding: "var(--mb-padding, var(--mantine-spacing-md))",
                        overflowX: "hidden",
                        ...(showCallEndedBanner ? { paddingTop: 0 } : { paddingTop: "var(--mantine-spacing-md)" }),
                    },
                }}
            >
                <Flex id="modal-heading" align="center" justify="space-between" mb="xl">
                    <Stack gap={0}>
                        <Text className={styles.modalTitle} fw="var(--mantine-font-weight-normal)" fz="sm">
                            {view === "schedule-callback" ? "Schedule Callback" : getSummaryHeader()}
                        </Text>
                        {!canCallback && (
                            <Text size="xs" c="warning.5">
                                You’ve reached the callback limit.
                            </Text>
                        )}
                    </Stack>
                </Flex>
                {view === "schedule-callback" ? (
                    <ScheduleCallbackForm shouldShowActions={false} />
                ) : (
                    <SessionSummaryForm
                        partner={partner}
                        loading={sessionSummaryLoading}
                        shouldDisableTextArea={shouldDisableTextArea}
                        shouldShowTextArea={shouldShowTextArea}
                        canSendSuccessfulSummary={canSendSuccessfulSummary}
                    />
                )}
                <SessionSummaryActions
                    onBackClick={() => setView("edit-summary")}
                    afterSubmitClickDefault={() => void callbackScheduleHandler()}
                    getButtonText={getSummaryButtonText}
                    setView={setView}
                    endSession={handleCopyOrEndSessionClick}
                    summaryUnavailable={sessionSummaryLoading || !summary}
                    callbackRemaining={callbacksRemaining}
                    shouldCopy={needsCopy}
                    expirySeconds={totalSeconds}
                    view={view}
                />
            </Modal>
        </Group>
    );
}
